<template>
    <!-- Mobile full-width: one continuous bar with the stepper arrows at the
         edges and the sheet trigger centered (v2 mobile period control). -->
    <div
        v-if="fullWidth"
        class="flex w-full items-stretch h-10 rounded-lg border border-border-base bg-bg-snow overflow-hidden"
    >
        <button
            type="button"
            class="px-3 flex items-center justify-center hover:bg-bg-subtle transition-colors"
            :class="{ 'opacity-40 cursor-not-allowed': !canStep }"
            :disabled="!canStep"
            :aria-label="`Previous ${current.label}`"
            @click="step(-1)"
        >
            <Icon icon="chevron-left" class="w-5 h-5 icons-primary" />
        </button>
        <button
            type="button"
            class="flex-1 min-w-0 flex items-center justify-center gap-1.5 hover:bg-bg-subtle transition-colors"
            @click="showSheet = true"
        >
            <Icon icon="calendar" class="w-5 h-5 icons-primary" />
            <span class="text-primary text-sm font-medium whitespace-nowrap">
                {{ current.label }}
            </span>
            <Icon icon="chevron-down-small" class="w-5 h-5 icons-primary" />
        </button>
        <button
            type="button"
            class="px-3 flex items-center justify-center hover:bg-bg-subtle transition-colors"
            :class="{ 'opacity-40 cursor-not-allowed': !canStep }"
            :disabled="!canStep"
            :aria-label="`Next ${current.label}`"
            @click="step(1)"
        >
            <Icon icon="chevron-right" class="w-5 h-5 icons-primary" />
        </button>

        <!-- Mobile period sheet -->
        <UiModalMobile
            :is-open="showSheet"
            title="Select period"
            @close="showSheet = false"
        >
            <template #content>
                <PeriodPanel
                    v-if="showSheet"
                    :value="current"
                    :multi-calendars="false"
                    :stacked="true"
                    @apply="onSheetApply"
                    @cancel="showSheet = false"
                />
            </template>
        </UiModalMobile>
    </div>

    <!-- Desktop: stepper pills around the popover trigger -->
    <div v-else class="inline-flex items-center gap-1">
        <!-- Previous period -->
        <button
            type="button"
            class="btn-base btn-white !px-2 h-10"
            :class="{ 'opacity-40 cursor-not-allowed': !canStep }"
            :disabled="!canStep"
            :aria-label="`Previous ${current.label}`"
            @click="step(-1)"
        >
            <Icon icon="chevron-left" class="w-5 h-5 icons-primary" />
        </button>

        <!-- Period panel (desktop popover) -->
        <ButtonPopover
            :teleport="true"
            :close-on-select="false"
            buttonClass="btn-base btn-white !pl-3 !pr-3 h-10"
            popoverClass="bg-bg-snow rounded-lg shadow-lg w-[800px] max-w-[95vw] z-40 outline outline-1 outline-offset-[-1px] outline-border-base overflow-hidden"
        >
            <template #button-content>
                <Icon icon="calendar" class="w-5 h-5 icons-primary" />
                <span class="text-primary text-sm font-medium whitespace-nowrap">
                    {{ current.label }}
                </span>
                <Icon
                    icon="chevron-down-small"
                    class="w-5 h-5 icons-primary"
                />
            </template>
            <template #default="{ closePopover }">
                <PeriodPanel
                    :value="current"
                    :multi-calendars="true"
                    @apply="(payload) => onPanelApply(payload, closePopover)"
                    @cancel="closePopover"
                />
            </template>
        </ButtonPopover>

        <!-- Next period -->
        <button
            type="button"
            class="btn-base btn-white !px-2 h-10"
            :class="{ 'opacity-40 cursor-not-allowed': !canStep }"
            :disabled="!canStep"
            :aria-label="`Next ${current.label}`"
            @click="step(1)"
        >
            <Icon icon="chevron-right" class="w-5 h-5 icons-primary" />
        </button>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import dayjs from 'dayjs';
import Icon from '@/components/ui/icon/icon.vue';
import ButtonPopover from '@/components/ui/button-popover.vue';
import UiModalMobile from '@/components/ui/modal/ui-modal-mobile.vue';
import PeriodPanel from './period-panel.vue';
import {
    PRESET_DEFS,
    labelForRange,
    stepRange,
} from './period-presets';

/**
 * Shared date/period control (Design System v2): a prev/next stepper around
 * a trigger that opens the date-filter panel — preset rail (This month /
 * Previous month / This year / Previous year / All time / Custom) plus an
 * inline range calendar with Cancel/Apply. Free of page-specific logic so
 * it can be reused beyond My Spending.
 *
 * v-model shape: { preset, start, end, label }
 *   - preset: a preset key, or 'custom'
 *   - start / end: 'YYYY-MM-DD' strings, or null for "All time"
 *   - label: the human label shown on the trigger
 */
const props = defineProps({
    modelValue: {
        type: Object,
        default: null,
    },
    /** Preset selected on first mount when no modelValue is provided. */
    defaultPreset: {
        type: String,
        default: 'all',
    },
    /** Stretch to fill the container (the center trigger grows). Used on mobile. */
    fullWidth: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue', 'change']);

// Mobile bottom-sheet (used only when fullWidth).
const showSheet = ref(false);

// Internal committed state (dayjs bounds + meta).
const current = ref({
    preset: props.defaultPreset,
    start: null,
    end: null,
    label: PRESET_DEFS[props.defaultPreset]?.label || 'All time',
});

const canStep = computed(() => !!current.value.start && !!current.value.end);

const labelFor = (preset, start, end) =>
    preset === 'all' || !start || !end
        ? 'All time'
        : labelForRange(start, end);

const emitValue = () => {
    const { preset, start, end, label } = current.value;
    const payload = {
        preset,
        start: start ? start.format('YYYY-MM-DD') : null,
        end: end ? end.format('YYYY-MM-DD') : null,
        label,
    };
    emit('update:modelValue', payload);
    emit('change', payload);
};

const selectPreset = (value) => {
    const def = PRESET_DEFS[value];
    if (!def) {
        return;
    }
    const { start, end } = def.range();
    current.value = {
        preset: value,
        start,
        end,
        label: value === 'all' ? def.label : labelFor(value, start, end),
    };
    emitValue();
};

const onPanelApply = (payload, close) => {
    current.value = {
        preset: payload.preset,
        start: payload.start,
        end: payload.end,
        label: labelFor(payload.preset, payload.start, payload.end),
    };
    emitValue();
    if (close) {
        close();
    }
};

const onSheetApply = (payload) => {
    onPanelApply(payload, null);
    showSheet.value = false;
};

// Step by the range's own granularity (months/years/day-span).
const step = (direction) => {
    if (!canStep.value) {
        return;
    }
    const { start, end } = stepRange(
        current.value.start,
        current.value.end,
        direction
    );
    current.value = {
        preset: 'custom',
        start,
        end,
        label: labelForRange(start, end),
    };
    emitValue();
};

// Hydrate internal state from an incoming modelValue (e.g. persisted filter).
// Unknown presets with dates (e.g. legacy '30'/'ytd' values) degrade to
// 'custom'; unknown without dates degrade to 'all'.
const hydrateFromModel = (value) => {
    if (!value || !value.preset) {
        return false;
    }
    const start = value.start ? dayjs(value.start) : null;
    const end = value.end ? dayjs(value.end) : null;
    const known = value.preset === 'custom' || !!PRESET_DEFS[value.preset];
    const preset = known ? value.preset : start && end ? 'custom' : 'all';
    current.value = {
        preset,
        start,
        end,
        label: value.label || labelFor(preset, start, end),
    };
    return true;
};

watch(
    () => props.modelValue,
    (value) => {
        // Only re-hydrate when the parent pushes a genuinely different value.
        if (!value) {
            return;
        }
        const sameStart =
            (current.value.start?.format('YYYY-MM-DD') || null) ===
            (value.start || null);
        const sameEnd =
            (current.value.end?.format('YYYY-MM-DD') || null) ===
            (value.end || null);
        if (value.preset === current.value.preset && sameStart && sameEnd) {
            return;
        }
        hydrateFromModel(value);
    }
);

onMounted(() => {
    // Adopt an incoming value, otherwise seed the default preset and emit so
    // the parent starts from a known range.
    if (!hydrateFromModel(props.modelValue)) {
        selectPreset(props.defaultPreset);
    }
});
</script>
